import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ReviewPendingChanges } from "../queries.graphql";
import type {
  ReviewPendingChangesMutation,
  ReviewPendingChangesMutationVariables,
  WorkerPendingChangesQuery,
} from "../queries.types";
import { SubmitHandler } from "react-hook-form";
import {
  PendingProfileChangeAttributeSchemaFormFields,
  PendingProfileChangeSchemaFormFields,
} from "@sinch/components/application/ProfileReviewDialog/formSchema";
import { isNotNil } from "ramda-adjunct";
import { PendingChangeDecisionEnum } from "@sinch/core/entities/serverEnums";
import { equals } from "ramda";
import {
  parseAttributeValue,
  serializeEditedValue,
} from "@sinch/components/application/Worker/Attributes/tools";
import { useSettings } from "@sinch/core/providers/AppSettings";

type PendingChange = WorkerPendingChangesQuery["pendingProfileChange"][number];

interface UseProfileReviewProps {
  workerId: string;
  onSuccess: () => void;
  // The raw server pending-change rows so we can diff each form value against
  // its original parsed shape and only forward `editedValue` when actually edited.
  changes: PendingChange[];
}

export const usePendingChangeReview = ({
  workerId,
  onSuccess,
  changes,
}: UseProfileReviewProps): {
  handleReviewChanges: (...args: Parameters<SubmitHandler<PendingProfileChangeSchemaFormFields>>) => Promise<boolean>;
  loading: boolean;
} => {
  const { timeZone } = useSettings();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const mutationOptions = {
    errorPolicy: "all" as const,
    awaitRefetchQueries: true,
    // WorkerPendingChanges is refetched so a rejected stale review (worker changed
    // the value mid-review) re-renders the dialog with the fresh values + revisions.
    refetchQueries: ["StaffDetail", "GetWorker", "WorkerPendingChanges"],
  };

  const [reviewChanges, { loading: reviewLoading }] = useMutation<
    ReviewPendingChangesMutation,
    ReviewPendingChangesMutationVariables
  >(ReviewPendingChanges, mutationOptions);

  const handleReviewChanges = useCallback<
    (...args: Parameters<SubmitHandler<PendingProfileChangeSchemaFormFields>>) => Promise<boolean>
  >(
    async (data) => {
      // Match each decision back to its original pending-change row so we can diff
      // the form value against the parsed default. Sending the parsed UI shape
      // (Date object, [{id,url,name}]) as `editedValue` on every approve would push
      // the wrong type at the backend; only forward `editedValue` when the admin
      // actually changed it, and re-serialise to a scalar/ID-array the resolver expects.
      const changesById = new Map(changes.map((change) => [change.id, change]));

      const decisions: ReviewPendingChangesMutationVariables["decisions"] = data.attributes
        .filter(
          (
            item
          ): item is Omit<PendingProfileChangeAttributeSchemaFormFields, "decision"> & {
            decision: PendingChangeDecisionEnum;
          } => isNotNil(item.decision)
        )
        .map((item) => {
          const change = changesById.get(item.changeId);
          const originalParsed = change
            ? parseAttributeValue(change.dataType ?? null, change.pendingValue, change.pendingFiles ?? [], timeZone)
            : null;
          const valueChanged = change !== undefined && !equals(originalParsed, item.value);
          return {
            changeId: item.changeId,
            // Echo back the content revision the admin actually reviewed. If the worker
            // re-submitted a different value in the meantime, the backend rejects the
            // stale decision instead of applying a value the admin never saw.
            revision: change?.revision ?? "",
            decision: item.decision,
            editedValue: valueChanged ? serializeEditedValue(change.dataType ?? null, item.value) : null,
            expirationDate: item.expirationDate,
            note: item.note,
          };
        });

      try {
        const response = await reviewChanges({ variables: { workerId, decisions } });

        if (response.data?.reviewPendingChanges.result) {
          enqueueSnackbar(t("ProfileReview.changesReviewed"), { variant: "success" });
          onSuccess();
          return true;
        } else {
          const messages = response.data?.reviewPendingChanges.messages;
          enqueueSnackbar(messages?.[0]?.message ?? t("ProfileReview.error"), { variant: "error" });
          return false;
        }
      } catch (e) {
        console.error(e);
        enqueueSnackbar(t("ProfileReview.error"), { variant: "error" });
        return false;
      }
    },
    [reviewChanges, workerId, enqueueSnackbar, t, onSuccess, changes, timeZone]
  );

  return {
    handleReviewChanges,
    loading: reviewLoading,
  };
};
